import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans } from '@easy-expense/ui-web-core';
import { setLightness } from 'polished';
import React from 'react';

/**
 * Maps a status color to the exact bg/text tokens used for that status
 * elsewhere in the app (e.g. ApprovalStatusSection, ReportApprovalSection),
 * rather than deriving a background/text pair algorithmically.
 */
const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  [theme.colors.success]: { bg: theme.colors.successXLight, text: theme.colors.successDark },
  [theme.colors.warning]: { bg: theme.colors.warningXLight, text: theme.colors.warningDark },
  [theme.colors.destructive]: {
    bg: theme.colors.destructiveXLight,
    text: theme.colors.destructiveDark,
  },
  [theme.colors.brandPrimary]: {
    bg: theme.colors.brandPrimaryXLight,
    text: theme.colors.brandPrimaryDark,
  },
  [theme.colors.gray]: { bg: theme.colors.grayXLight, text: theme.colors.grayDark },
};

/**
 * Status pill for statuses that already have dedicated colors defined in the
 * theme (approval/report/expense status, etc). Unlike Badge, colors are used
 * as-is instead of being computed with lighten/darken, so a status pill
 * always matches the color used for that same status elsewhere in the app.
 */
export const StatusBadge: React.FC<{
  color: string;
  text: string;
}> = ({ color, text }) => {
  const { bg, text: textColor } = STATUS_COLORS[color] ?? {
    bg: setLightness(0.92, color),
    text: setLightness(0.3, color),
  };

  return (
    <Layout.Row
      style={{
        backgroundColor: bg,
        alignSelf: 'flex-start',
        width: 'fit-content',
      }}
      px={8}
      py={4}
      radius={4}
    >
      <OpenSans.Primary size="s-12" style={{ overflow: 'hidden', color: textColor }}>
        {text}
      </OpenSans.Primary>
    </Layout.Row>
  );
};
